import { BookOpen, ChevronDown, Search, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks";
import { noDash } from "../insights";

export type LibraryPrompt = {
  id: number;
  prompt_name: string;
  prompt_body: string;
  model_tier: string;
  category_name: string;
  category_slug: string;
  color_hex: string | null;
};

/** "sonnet" → "Sonnet": the model's name, not the stored value. */
export const tierLabel = (tier: string | null | undefined) => (tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : "");

/** The {{placeholders}} and [PLACEHOLDERS] a prompt expects someone to fill in. */
export function promptVariables(body: string): string[] {
  const found = body.match(/\{\{[^}]+\}\}|\[[A-Z][A-Z0-9 _-]{2,}\]/g) || [];
  return [...new Set(found)];
}

/**
 * Picks a Prompt Library prompt for the next analyst message. The analyst API
 * puts the prompt ahead of the message, so what is typed becomes the details
 * the prompt asks for.
 */
export default function PromptPicker({
  prompts,
  selected,
  onSelect,
  disabled,
  scope,
}: {
  prompts: ReturnType<typeof useApi<LibraryPrompt[]>>;
  selected: LibraryPrompt | undefined;
  onSelect: (prompt: LibraryPrompt | null) => void;
  disabled?: boolean;
  /** Narrows the list to the prompts that fit this page; "Show all prompts" lifts it. */
  scope?: { label: string; fits: (prompt: LibraryPrompt) => boolean };
}) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (open) searchRef.current?.focus(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey, true); };
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, { name: string; color: string | null; items: LibraryPrompt[] }>();
    for (const p of Array.isArray(prompts.data) ? prompts.data : []) {
      if (scope && !all && !scope.fits(p)) continue;
      if (q && !`${p.prompt_name} ${p.category_name} ${p.prompt_body}`.toLowerCase().includes(q)) continue;
      const g = map.get(p.category_slug) || { name: p.category_name, color: p.color_hex, items: [] };
      g.items.push(p);
      map.set(p.category_slug, g);
    }
    return [...map.values()];
  }, [prompts.data, query, scope, all]);

  const variables = selected ? promptVariables(selected.prompt_body) : [];

  return (
    <div className="pp-root" ref={rootRef}>
      {selected ? (
        <div className="pp-selected">
          <div className="pp-selected-head">
            <span className="pl-cat"><i style={{ background: selected.color_hex || "var(--text-faint)" }} aria-hidden="true" />{selected.category_name}</span>
            <strong>{noDash(selected.prompt_name)}</strong>
            <span className={`pl-tier t-${selected.model_tier}`}><Sparkles aria-hidden="true" />{tierLabel(selected.model_tier)}</span>
            <button type="button" className="text-button" onClick={() => setPreview((v) => !v)} aria-expanded={preview}>
              {preview ? "Hide prompt" : "Show prompt"}
            </button>
            <button type="button" className="icon-button pp-remove" onClick={() => { onSelect(null); setPreview(false); }} aria-label="Stop using this prompt" disabled={disabled}>
              <X aria-hidden="true" />
            </button>
          </div>
          {variables.length > 0 && (
            <p className="pp-vars">Add these in your message: {variables.map((v) => <mark key={v}>{v}</mark>)}</p>
          )}
          {preview && <pre className="pl-body small pp-preview">{selected.prompt_body}</pre>}
        </div>
      ) : null}

      <button
        type="button"
        className="button secondary pp-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
      >
        <BookOpen aria-hidden="true" />
        {selected ? "Change prompt" : "Use a prompt"}
        <ChevronDown aria-hidden="true" />
      </button>

      {open && (
        <div className={scope ? "pp-menu scoped" : "pp-menu"} role="dialog" aria-label="Choose a prompt from the library">
          <label className="search-control pp-search">
            <Search aria-hidden="true" />
            <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the prompt library…" aria-label="Search prompts" />
          </label>
          {scope && (
            <p className="pp-scope">
              <span>{all ? "All prompts" : scope.label}</span>
              <button type="button" className="text-button" onClick={() => setAll((v) => !v)}>{all ? `Only ${scope.label.toLowerCase()}` : "Show all prompts"}</button>
            </p>
          )}
          <div className="pp-list" role="listbox" aria-label="Prompts">
            {prompts.loading && !prompts.data ? (
              <p className="pp-empty">Loading prompts…</p>
            ) : prompts.error ? (
              <p className="pp-empty">Could not load prompts. <button type="button" className="text-button" onClick={prompts.reload}>Retry</button></p>
            ) : !groups.length ? (
              <p className="pp-empty">{query ? "No prompts match that search." : "The prompt library is empty."}</p>
            ) : groups.map((g) => (
              <div key={g.name} className="pp-group">
                <p className="pp-group-name"><i style={{ background: g.color || "var(--text-faint)" }} aria-hidden="true" />{g.name}</p>
                {g.items.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={selected?.id === p.id}
                    className={selected?.id === p.id ? "pp-option active" : "pp-option"}
                    onClick={() => { onSelect(p); setOpen(false); setQuery(""); }}
                  >
                    <strong>{noDash(p.prompt_name)}</strong>
                    <span>{p.prompt_body.replace(/\s+/g, " ").slice(0, 120)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
          <Link className="pp-library-link" to="/prompts">Open the prompt library</Link>
        </div>
      )}
    </div>
  );
}
